<template>
    <div class="multiselect" v-click-outside="closeDropdown">
        <button 
            class="multiselect-trigger" 
            @click="toggleDropdown"
            :aria-label="`${label} selection. ${selectedValues.length} items selected.`"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
        >
            <span class="multiselect-label">{{ displayText }}</span>

            <span class="dropdown-icon" :class="{ 'is-open': isOpen }" aria-hidden="true"></span>
        </button>
        
        <transition name="dropdown">
            <div 
                v-if="isOpen" 
                class="multiselect-dropdown"
                role="listbox"
                :aria-labelledby="`multiselect-trigger-${_uid}`"
                aria-multiselectable="true"
            >
                <div class="dropdown-items">
                    <div 
                        v-for="(item, index) in items" 
                        :key="index"
                        class="dropdown-item"
                        role="option"
                        :aria-selected="isSelected(item)"
                    >
                        <label :for="`checkbox-${_uid}-${index}`" class="dropdown-item-label">
                            <div class="checkbox-wrapper">
                                <input 
                                    type="checkbox" 
                                    :id="`checkbox-${_uid}-${index}`"
                                    :checked="isSelected(item)"
                                    @change="toggleItem(item)"
                                    :aria-describedby="`item-label-${_uid}-${index}`"
                                />
                                <span class="checkbox-custom">
                                    <i v-if="isSelected(item)" class="checkmark" aria-hidden="true"></i>
                                </span>
                            </div>
                            <span class="item-label" :id="`item-label-${_uid}-${index}`">{{ getItemLabel(item) }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import adaMixin from '../js/mixins/adaMixin.js';

export default {
    name: 'menu-multiselect',
    mixins: [adaMixin],
    props: {
        value: {
            type: Array,
            default: () => []
        },
        items: {
            type: Array,
            required: true
        },
        label: {
            type: String,
            required: true
        },
    },
    data() {
        return {
            isOpen: false,
            selectedValues: [...this.value]
        };
    },
    computed: {
        displayText() {
            if (this.selectedValues.length === 0) {
                return this.label;
            }
            // Map selected values back to their labels
            const selectedLabels = this.selectedValues.map(value => {
                const item = this.items.find(item => this.getItemValue(item) === value);
                return item ? this.getItemLabel(item) : value;
            });
            return selectedLabels.join(', ');
        }
    },
    watch: {
        value(newVal) {
            this.selectedValues = [...newVal];
        }
    },
    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen;
        },
        closeDropdown() {
            this.isOpen = false;
        },
        toggleItem(item) {
            const itemValue = this.getItemValue(item);
            const itemLabel = this.getItemLabel(item);
            const index = this.selectedValues.indexOf(itemValue);
            
            if (index > -1) {
                this.selectedValues.splice(index, 1);
                this.$emit('input', [...this.selectedValues]);
                // Announce deselection
                this.setAndClearAnnouncement(`${itemLabel} deselected. ${this.selectedValues.length} ${this.label.toLowerCase()} selected.`);
            } else {
                this.selectedValues.push(itemValue);
                this.$emit('input', [...this.selectedValues]);
                // Announce selection
                this.setAndClearAnnouncement(`${itemLabel} selected. ${this.selectedValues.length} ${this.label.toLowerCase()} selected.`);
            }
        },
        isSelected(item) {
            const itemValue = this.getItemValue(item);
            return this.selectedValues.includes(itemValue);
        },
        getItemLabel(item) {
            // If item is an object with label property, return label
            // Otherwise treat it as a string
            return typeof item === 'object' && item.label ? item.label : item;
        },
        getItemValue(item) {
            // If item is an object with value property, return value
            // Otherwise treat it as a string
            return typeof item === 'object' && item.value !== undefined ? item.value : item;
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unbind(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    }
};
</script>

<style scoped>
.multiselect {
    position: relative;   
}

.multiselect-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 10px;
    background-color: #F6F6F7;
    border: none;
    border-radius: 14px;
    cursor: pointer;
    height: 40px;
    font-size: 16px;
    color: #333333;
    width: 100%;
}

.multiselect-label {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dropdown-icon {
    margin-left: 5px;
    transition: transform 300ms ease;
}

.dropdown-icon::before {
    font-size: 10px;
    content: '\e916';
    font-family: 'e-icons';
}

.dropdown-icon.is-open {
    transform: rotate(180deg);
    transition: transform 300ms ease;
}

.multiselect-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    min-width: 150px;
}

@media (max-width: 576px) {
    .multiselect-dropdown {
        min-width: 100%;
        left: 0;
        right: 0;
    }
}

.dropdown-items {
    padding: 8px 0;
}

.dropdown-item {
    padding: 0;
}

.dropdown-item-label {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.dropdown-item-label:hover {
    background-color: #F5F5F5;
}

.checkbox-wrapper {
    position: relative;
    margin-right: 12px;
}

.checkbox-wrapper input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
}

.checkbox-custom {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid #E1E1E2;
    border-radius: 4px;
    background-color: #F6F6F7;
    position: relative;
    transition: all 0.2s ease;
}

.checkbox-wrapper input[type="checkbox"]:checked ~ .checkbox-custom {
    background-color: #C2E66E;
    border-color: #C2E66E;
}

.checkmark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.checkmark::after {
    content: '';
    display: block;
    width: 4px;
    height: 7px;
    border: solid black;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-1px, -1px);
}

.item-label {
    font-size: 14px;
    color: #333333;
    cursor: pointer;
    user-select: none;
    font-family: 'InclusiveSansZero', sans-serif;
    flex: 1;
}

.dropdown-enter-active,
.dropdown-leave-active {
    transition: all 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.multiselect-dropdown::-webkit-scrollbar {
    width: 6px;
}

.multiselect-dropdown::-webkit-scrollbar-track {
    background: transparent;
}

.multiselect-dropdown::-webkit-scrollbar-thumb {
    background: rgba(138, 140, 144, 0.5);
    border-radius: 3px;
}

.multiselect-dropdown::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 140, 144, 0.8);
}

/* Override focus styles for multiselect trigger to use box-shadow instead of outline */
.multiselect-trigger:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px #C2E66E !important;
}
</style>